<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import SpinningWheel from './components/SpinningWheel.vue';
import RouteMap from './components/RouteMap.vue';
import ZonePicker from './components/ZonePicker.vue';
import LoginForm from './components/LoginForm.vue';
import ChangePassword from './components/ChangePassword.vue';
import UploadUserData from './components/UploadUserData.vue';
import { filterRoutes, computeLocationSuggestions } from './utils/routeFilter';
import { getSession, signOut } from './utils/auth';
import { fetchUserRoutes, getSignedImageUrl } from './utils/userdata';

// ── Auth state ──────────────────────────────────────────────
// 'checking' | 'unauthenticated' | 'new-password-required' | 'change-password' | 'authenticated' | 'upload'
const authState = ref('checking');
const challengeUser = ref(null);

// ── Routes from S3 ──────────────────────────────────────────
const userGroup = ref(null);
const routes = ref([]);
const routeLoadError = ref('');
const signedImageCache = ref({});

const presignAllImages = async (routeList, group) => {
  const cache = {};
  const extractFilename = (value) => {
    if (typeof value !== 'string' || value.startsWith('http')) return null;
    const cleaned = value.replace(/^file:\/\//, '').replace(/^\.?\//, '');
    const m = cleaned.match(/(?:data\/(?:images|mages)|images)\/(.+)$/);
    return m ? m[1] : cleaned;
  };
  const filenames = new Set();
  for (const route of routeList) {
    const uris = [
      ...(Array.isArray(route.imageUris) ? route.imageUris : []),
      ...(Array.isArray(route.imageUri) ? route.imageUri : route.imageUri ? [route.imageUri] : []),
    ];
    for (const uri of uris) {
      const fn = extractFilename(uri);
      if (fn) filenames.add(fn);
    }
  }
  await Promise.all(
    [...filenames].map(async (fn) => {
      try {
        cache[fn] = await getSignedImageUrl(group, fn);
      } catch (e) {
        console.warn('Failed to pre-sign image', fn, e);
      }
    }),
  );
  signedImageCache.value = cache;
};

const loadUserRoutes = async () => {
  routeLoadError.value = '';
  if (import.meta.env.VITE_ROUTE_USERDATA) {
    try {
      const result = await fetchUserRoutes();
      routes.value = result.routes;
      userGroup.value = result.group;
      await presignAllImages(result.routes, result.group);
    } catch (err) {
      console.error('Failed to load routes from S3:', err);
      routeLoadError.value = `Could not load routes: ${err.message}`;
    }
  }
};

onMounted(async () => {
  try {
    await getSession();
    authState.value = 'authenticated';
    await loadUserRoutes();
  } catch {
    authState.value = 'unauthenticated';
  }
});

const handleLoginSuccess = async () => {
  authState.value = 'authenticated';
  await loadUserRoutes();
};
const handlePasswordChangeRequired = (user) => {
  challengeUser.value = user;
  authState.value = 'new-password-required';
};
const handlePasswordChanged = async () => {
  challengeUser.value = null;
  await handleLoginSuccess();
};
const handleSignOut = () => {
  signOut();
  authState.value = 'unauthenticated';
  showWheel.value = false;
  selectedRoute.value = null;
  location.value = null;
  zonePickerKey.value++;
  routes.value = [];
  userGroup.value = null;
  signedImageCache.value = {};
};

const clusters = computed(() => computeLocationSuggestions(routes.value));

// ── Setup state ────────────────────────────────────────────
const showWheel = ref(false);
const location = ref(null);
const zonePickerKey = ref(0);

const filteredRoutes = computed(() => {
  if (!location.value) return [];
  return filterRoutes(routes.value, location.value);
});

const handleZoneSelect = ({ lat, lon }) => {
  location.value = { lat, lon };
};

const handleFindRoutes = () => { showWheel.value = true; };

const handleRestart = () => {
  showWheel.value = false;
  selectedRoute.value = null;
  activeImageIndex.value = 0;
  location.value = null;
  zonePickerKey.value++;
};

// ── Route selection state ──────────────────────────────────
const selectedRoute = ref(null);
const activeImageIndex = ref(0);
const routeDetailsEl = ref(null);

const handleRouteSelection = (route) => {
  selectedRoute.value = route;
  activeImageIndex.value = 0;
  nextTick(() => {
    routeDetailsEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
};

const hasGpxTrack = (route) => Array.isArray(route?.gpx?.track) && route.gpx.track.length > 1;
const normalizeImageUri = (value) => {
  if (typeof value !== 'string' || value.length === 0) {
    return '';
  }

  if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) {
    return value;
  }

  const cleaned = value.replace(/^file:\/\//, '').replace(/^\.?\//, '');

  // Look up pre-signed URL from cache when bucket + group are available
  if (import.meta.env.VITE_ROUTE_USERDATA && userGroup.value) {
    const m = cleaned.match(/(?:data\/(?:images|mages)|images)\/(.+)$/);
    const filename = m ? m[1] : cleaned;
    return signedImageCache.value[filename] ?? '';
  }

  const dataPathMatch = cleaned.match(/data\/(?:images|mages)\/(.+)$/);
  if (dataPathMatch) {
    return `/data/images/${dataPathMatch[1]}`;
  }

  const imagesPathMatch = cleaned.match(/^images\/(.+)$/);
  if (imagesPathMatch) {
    return `/src/data/images/${imagesPathMatch[1]}`;
  }

  return `/${cleaned}`;
};

const routeImages = (route) => {
  const imageSources = [];

  if (Array.isArray(route?.imageUris)) {
    imageSources.push(...route.imageUris);
  }

  if (Array.isArray(route?.imageUri)) {
    imageSources.push(...route.imageUri);
  } else if (typeof route?.imageUri === 'string' && route.imageUri.length > 0) {
    imageSources.push(route.imageUri);
  }

  return imageSources
    .map(normalizeImageUri)
    .filter((value) => typeof value === 'string' && value.length > 0);
};

const selectedRouteImages = computed(() => routeImages(selectedRoute.value));
const activeImageUri = computed(() => {
  if (!selectedRouteImages.value.length) {
    return '';
  }

  const safeIndex = Math.min(activeImageIndex.value, selectedRouteImages.value.length - 1);
  return selectedRouteImages.value[safeIndex];
});

watch(selectedRouteImages, (images) => {
  if (!images.length || activeImageIndex.value > images.length - 1) {
    activeImageIndex.value = 0;
  }
});
</script>

<template>
  <!-- Auth: checking session -->
  <div
    v-if="authState === 'checking'"
    class="auth-page"
  >
    <div class="auth-card auth-loading">
      <p>Loading…</p>
    </div>
  </div>

  <!-- Auth: login -->
  <LoginForm
    v-else-if="authState === 'unauthenticated'"
    @login-success="handleLoginSuccess"
    @password-change-required="handlePasswordChangeRequired"
  />

  <!-- Auth: forced new-password (first login) -->
  <ChangePassword
    v-else-if="authState === 'new-password-required'"
    :challenge-user="challengeUser"
    @password-changed="handlePasswordChanged"
  />

  <!-- Auth: voluntary password change -->
  <ChangePassword
    v-else-if="authState === 'change-password'"
    @password-changed="handlePasswordChanged"
    @cancel="authState = 'authenticated'"
  />

  <!-- Upload user data -->
  <UploadUserData
    v-else-if="authState === 'upload'"
    @close="authState = 'authenticated'; loadUserRoutes()"
  />

  <!-- Main app -->
  <div
    v-else
    class="app"
  >
    <div class="app-header">
      <div class="app-brand">
        <svg
          class="app-brand-mark"
          viewBox="0 0 96 32"
          role="img"
          aria-label="wanderly adventure mark"
        >
          <defs>
            <linearGradient
              id="wanderlyMarkGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop
                offset="0%"
                stop-color="#3f6f78"
              />
              <stop
                offset="50%"
                stop-color="#5b8a72"
              />
              <stop
                offset="100%"
                stop-color="#c7a96a"
              />
            </linearGradient>
          </defs>
          <path
            d="M2 24 Q 16 8 30 18 T 58 18 T 94 10"
            fill="none"
            stroke="url(#wanderlyMarkGradient)"
            stroke-width="4"
            stroke-linecap="round"
          />
          <circle
            cx="78"
            cy="10"
            r="3"
            fill="#c7a96a"
          />
          <path
            d="M71 27 l7 -8 l8 8"
            fill="none"
            stroke="#3f6f78"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <h1>wanderly</h1>
      </div>
      <div class="app-header-actions">
        <button
          class="btn-link"
          @click="authState = 'upload'"
        >
          Upload Data
        </button>
        <button
          class="btn-link"
          @click="authState = 'change-password'"
        >
          Change Password
        </button>
        <button
          class="btn-secondary"
          @click="handleSignOut"
        >
          Sign Out
        </button>
      </div>
    </div>

    <!-- Route load error -->
    <div
      v-if="routeLoadError"
      class="route-load-error"
    >
      <p>{{ routeLoadError }}</p>
      <button
        class="btn-link"
        @click="loadUserRoutes"
      >
        Retry
      </button>
    </div>

    <div class="app-main">
      <div class="app-workspace">
        <!-- Setup screen -->
        <div
          v-if="!showWheel"
          class="filters"
        >
          <div class="filter-group">
            <span class="filter-label">Starting Location</span>
            <ZonePicker
              :key="zonePickerKey"
              :clusters="clusters"
              @zone-select="handleZoneSelect"
              @zone-confirm="handleFindRoutes"
            />
            <p
              v-if="location"
              class="zone-selected-hint"
            >
              ✓ {{ filteredRoutes.length }} routes — tap the zone again to spin
            </p>
          </div>
        </div>

        <!-- Wheel screen -->
        <template v-else>
          <div
            v-if="filteredRoutes.length === 0"
            class="no-routes"
          >
            <p>No routes found within 10 km of that zone.</p>
            <button
              class="btn-secondary"
              @click="handleRestart"
            >
              Change Filters
            </button>
          </div>
          <template v-else>
            <SpinningWheel
              :routes="filteredRoutes"
              @route-select="handleRouteSelection"
            />
            <div class="wheel-actions">
              <button
                class="btn-secondary"
                @click="handleRestart"
              >
                ↩ Start Over
              </button>
            </div>
          </template>
        </template>
      </div>
      <div
        v-if="selectedRoute"
        ref="routeDetailsEl"
        class="route-details"
      >
        <h2>Selected Route</h2>
        <p><strong>Start:</strong> {{ selectedRoute.start }}</p>
        <p><strong>Stop:</strong> {{ selectedRoute.stop }}</p>
        <p><strong>Distance:</strong> {{ selectedRoute.distance }}</p>
        <p><strong>Steps:</strong> {{ selectedRoute.steps }}</p>
        <p><strong>Time:</strong> {{ selectedRoute.time }}</p>
        <div v-if="hasGpxTrack(selectedRoute)">
          <h3>Route Map</h3>
          <RouteMap :track="selectedRoute.gpx.track" />
        </div>
        <div
          v-if="selectedRouteImages.length"
          class="route-gallery"
        >
          <h3>Route Photos</h3>
          <div class="lightbox-panel">
            <a
              :href="activeImageUri"
              target="_blank"
              rel="noopener noreferrer"
              class="lightbox-stage-link"
            >
              <img
                :src="activeImageUri"
                :alt="`${selectedRoute.name} photo ${activeImageIndex + 1}`"
                class="lightbox-stage-image"
              >
            </a>
            <p class="lightbox-counter">
              Photo {{ activeImageIndex + 1 }} of {{ selectedRouteImages.length }}
            </p>
            <div class="lightbox-thumbnails">
              <button
                v-for="(imageUri, index) in selectedRouteImages"
                :key="`${selectedRoute.name}-${index}`"
                type="button"
                :class="['lightbox-thumb-button', { 'is-active': index === activeImageIndex }]"
                @click="activeImageIndex = index"
              >
                <img
                  :src="imageUri"
                  :alt="`${selectedRoute.name} preview ${index + 1}`"
                  class="lightbox-thumb-image"
                >
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
